import { ReportRow } from "./types";
import { sumBy, earliest, latest, computeEndingBalances } from "./utils";

/**
 * Collapses report rows sharing one InvoiceTracker PK
 * (resellerId#productCode#channelCode) into a single combined parent row so the
 * shared invoice is reconciled/deducted exactly once.
 *
 * Configs that share a PK are invoiced together against ONE tracker record, so
 * reconciling them individually deducts that invoice once per config (the
 * claroco 5x over-count). Combining them means reconcileRow runs once on the
 * parent and the shared invoice is deducted once.
 *
 * - Rows whose pkOf() is null (unknown productCode) pass through untouched.
 * - PK groups with a single member pass through untouched (no subRows).
 * - PK groups with >1 member become one parent row summing the economic
 *   quantities; the original per-config rows are attached as subRows for
 *   expandable detail. If the members' deal start/end dates disagree, the parent
 *   is still combined (so the single shared invoice reconciles once) but flagged
 *   dateMismatch=true for human review.
 * First-occurrence order is preserved.
 *
 * INVARIANT: callers must aggregate the returned top-level rows only --
 * `subRows` is per-config display detail whose amounts are already summed into
 * the parent, so iterating parent + children together would double-count.
 */
export function combineRowsByTrackerPK(
  rows: ReportRow[],
  pkOf: (row: ReportRow) => string | null,
): ReportRow[] {
  const order: ReportRow[][] = [];
  const byPk = new Map<string, ReportRow[]>();
  for (const row of rows) {
    const pk = pkOf(row);
    if (!pk) {
      order.push([row]); // null-PK: passthrough, its own slot
      continue;
    }
    const g = byPk.get(pk);
    if (g) {
      g.push(row);
    } else {
      const ng = [row];
      byPk.set(pk, ng);
      order.push(ng);
    }
  }
  return order.map((g) => (g.length <= 1 ? g[0] : combineGroup(g)));
}

function combineGroup(members: ReportRow[]): ReportRow {
  const base = members[0];

  const currencies = new Set(members.map((m) => m.currencyCode));
  if (currencies.size > 1) {
    // Amounts under one tracker PK should share a currency; summing across
    // currencies would be meaningless. Not observed in data, but guard + warn.
    console.warn(
      `combineRowsByTrackerPK: mixed currencyCode [${[...currencies].join(
        ", ",
      )}] for reseller ${base.resellerId}; using ${base.currencyCode}`,
    );
  }

  // Configs sharing a tracker PK are invoiced together and are expected to cover
  // the same deal period. If their dates disagree we still combine (so the single
  // shared invoice reconciles once) but flag the parent for human review -- there
  // is no reliable way to tell which config's dates are wrong, so the operator
  // resolves it in FACD.
  const startDates = new Set(members.map((m) => m.dealStartDate));
  const endDates = new Set(members.map((m) => m.dealEndDate));
  const dateMismatch = startDates.size > 1 || endDates.size > 1;
  if (dateMismatch) {
    console.warn(
      `combineRowsByTrackerPK: ${members.length} configs for reseller ` +
        `${base.resellerId} share a tracker PK but have differing deal dates ` +
        `(starts: [${[...startDates].join(", ")}], ends: ` +
        `[${[...endDates].join(", ")}]); combined for invoice reconciliation but ` +
        `flagged for review -- verify configuration.`,
    );
  }

  const combined: ReportRow = {
    ...base, // inherit identity: companyCode, region, currencyCode, fxRate, funcCurrency, serviceProvider, channelCode
    fixedFeeId: `${members.length} configs combined (${base.resellerId})`,
    dateMismatch,
    // Parent spans widest coverage; each child keeps its own exact dates in subRows.
    dealStartDate: earliest(members.map((m) => m.dealStartDate)),
    dealEndDate: latest(members.map((m) => m.dealEndDate)),
    effortType: members.every((m) => m.effortType === "Automatic")
      ? "Automatic"
      : "Manual",
    contractPrice: sumBy(members, (m) => m.contractPrice),
    stIssuance: sumBy(members, (m) => m.stIssuance),
    ltIssuance: sumBy(members, (m) => m.ltIssuance),
    cumulativeReclass: sumBy(members, (m) => m.cumulativeReclass),
    invoicesIssued: sumBy(members, (m) => m.invoicesIssued), // baseline; reconcile overwrites once
    // Load-bearing: base carries a non-zero value; must re-zero so
    // populateFunctionalFields re-derives from the combined invoicesIssued.
    // (The other 8 func fields are overwritten unconditionally downstream.)
    functionalInvoicesIssued: 0,
    firstInvoiceBreakdownPeriod: earliest(
      members.map((m) => m.firstInvoiceBreakdownPeriod),
    ),
    tacActiveBillingPeriods: unionTac(members),
    invoiceStatus: "unreconciled",
    trackerTotal: 0,
    subRows: members,
    stEnding: 0,
    ltEnding: 0, // set immediately below
  };
  Object.assign(combined, computeEndingBalances(combined));
  return combined;
}

function unionTac(members: ReportRow[]): string[] | undefined {
  const u = Array.from(
    new Set(members.flatMap((m) => m.tacActiveBillingPeriods ?? [])),
  ).sort();
  return u.length ? u : undefined;
}
